import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";
import { AppError } from "@obg/ngx.core";

import {
	BrRegistrationPayload,
	CPFRegistrationPayload,
	Credential,
	RegistrationPayload,
	RegistrationResponse,
	RegistrationStatusResponse,
	RegistrationVerificationResponse,
} from "./registration.model";

const ACTION_PREFIX = "[Registration]";

export const REGISTRATION_ACTION_TYPE = {
	getAllRegistrationTokens: `${ACTION_PREFIX} Get All Registration Tokens`,
	getAllRegistrationTokensSuccess: `${ACTION_PREFIX} Get All Registration Tokens Success`,
	getAllRegistrationTokensFail: `${ACTION_PREFIX} Get All Registration Tokens Fail`,

	/**
	 * Dispatched to start registration process.
	 */
	getRegistrationToken: `${ACTION_PREFIX} Get Registration Token`,
	getCPFRegistrationToken: `${ACTION_PREFIX} Get CPF Registration Token `,
	getRegistrationTokenSuccess: `${ACTION_PREFIX} Get Registration Token Success`,
	getRegistrationTokenFail: `${ACTION_PREFIX} Get Registration Token Fail`,

	/**
	 * Dispatched to retrieve registration status for a specific token.
	 */
	getRegistrationStatus: `${ACTION_PREFIX} Get Registration Status`,
	getRegistrationStatusSuccess: `${ACTION_PREFIX} Get Registration Status Success`,
	getRegistrationStatusFail: `${ACTION_PREFIX} Get Registration Status Fail`,

	getAllRegistrationVerifications: `${ACTION_PREFIX} Get All Registration Verifications`,
	getAllRegistrationVerificationsSuccess: `${ACTION_PREFIX} Get All Registration Verifications Success`,
	getAllRegistrationVerificationsFail: `${ACTION_PREFIX} Get All Registration Verifications Fail`,

	verifyRegistrationToken: `${ACTION_PREFIX} Verify Registration Token`,
	verifyRegistrationTokenSuccess: `${ACTION_PREFIX} Verify Registration Token Success`,
	verifyRegistrationTokenFail: `${ACTION_PREFIX} Verify Registration Token Fail`,

	/**
	 * Dispatched when the registration flow is finished.
	 */
	registrationSuccess: `${ACTION_PREFIX} Register Success`,
	registrationFail: `${ACTION_PREFIX} Register Fail`,
	registrationFailWithSelfExclusion: `${ACTION_PREFIX} Register With Self-Exclusion Fail`,
	registrationFinished: `${ACTION_PREFIX} Register Finished`,

	/**
	 * Dispatched to reset registration state.
	 */
	resetRegistrationState: `${ACTION_PREFIX} Reset State ${SYNC_SUFFIX}`,

	setRegistrationDetails: `${ACTION_PREFIX} Set Details ${SYNC_SUFFIX}`,
	setInitialStep: `${ACTION_PREFIX} Set Initial Step ${SYNC_SUFFIX}`,
	clearRegistrationDetails: `${ACTION_PREFIX} Clear Details ${SYNC_SUFFIX}`,
};

@Injectable({
	providedIn: "root",
})
export class RegistrationAction {
	getAllRegistrationTokens(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationTokens,
		};
	}

	getAllRegistrationTokensSuccess(response: any): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationTokensSuccess,
			payload: response,
		};
	}

	getAllRegistrationTokensFail(error: AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationTokensFail,
			payload: error,
		};
	}

	getRegistrationStatus(payload: RegistrationResponse): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationStatus,
			payload,
		};
	}

	getRegistrationStatusSuccess(response: RegistrationStatusResponse, credential?: Credential): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess,
			payload: {
				status: response.status,
				reasons: response.reasons,
				credential,
			},
		};
	}

	getRegistrationStatusFail(error: AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationStatusFail,
			payload: error,
		};
	}

	getRegistrationToken(payload: RegistrationPayload | BrRegistrationPayload): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationToken,
			payload,
		};
	}

	getCPFRegistrationToken(payload: CPFRegistrationPayload): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getCPFRegistrationToken,
			payload,
		};
	}

	getRegistrationTokenSuccess(response: RegistrationResponse, credential: Credential): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess,
			payload: {
				token: response.token,
				hashedEmail: response.hashedEmail,
				credential,
			},
		};
	}

	getRegistrationTokenFail(error: AppError): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.getRegistrationTokenFail,
			payload: error,
		};
	}

	getAllRegistrationVerifications(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationVerifications,
		};
	}

	getAllRegistrationVerificationsSuccess(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationVerificationsSuccess,
		};
	}

	getAllRegistrationVerificationsFail(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.getAllRegistrationVerificationsFail,
		};
	}

	verifyRegistrationToken(token: string): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.verifyRegistrationToken,
			payload: token,
		};
	}

	verifyRegistrationTokenSuccess(payload: RegistrationVerificationResponse): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.verifyRegistrationTokenSuccess,
			payload,
		};
	}

	verifyRegistrationTokenFail(error: AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.verifyRegistrationTokenFail,
			payload: error,
		};
	}

	registrationSuccess(payload: RegistrationStatusResponse): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.registrationSuccess,
			payload,
		};
	}

	registrationFail(error: RegistrationStatusResponse | AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.registrationFail,
			payload: error,
		};
	}

	registrationFailWithSelfExclusion(error: RegistrationStatusResponse | AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion,
			payload: error,
		};
	}

	registrationFinished(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.registrationFinished,
		};
	}

	resetRegistrationState(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.resetRegistrationState,
		};
	}

	setRegistrationDetails(payload: RegistrationPayload | BrRegistrationPayload): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.setRegistrationDetails,
			payload,
		};
	}

	setInitialStep(payload: number): ActionWithPayload {
		return {
			type: REGISTRATION_ACTION_TYPE.setInitialStep,
			payload,
		};
	}

	clearRegistrationDetails(): Action {
		return {
			type: REGISTRATION_ACTION_TYPE.clearRegistrationDetails,
		};
	}
}
